import { cn, IconButton, Separator } from "@beweegbaas/ui";
import { useAtomSet } from "@effect/atom-react";
import { getLocalTimeZone, today } from "@internationalized/date";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { containerStyles, Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { PlanningHeader } from "#/components/planning-header";
import { ConfirmButton } from "#/components/confirm-button";
import { JaarplanningGrid } from "#/modules/les.planning/components/jaarplanning-grid";
import { JaarplanningOverview } from "#/modules/les.planning/components/jaarplanning-overview";
import {
  getSchooljaarRange,
  getWeekStart,
  toAbsoluteIso,
} from "#/modules/les.planning/domain/calendar";
import { scrollGridToWeek } from "#/modules/les.planning/domain/jaarplanning/scroll";
import {
  getPlanningWeeks,
  getStandaardWeeks,
} from "#/modules/les.planning/domain/jaarplanning/vakanties";
import { getUitLessen } from "#/modules/les.planning/domain/jaarplanning/uit-lessen";
import {
  EMPTY_JAARPLANNING,
  getAantalLessen,
  STANDAARD_VAK,
} from "#/modules/les.planning/domain/jaarplanning/vakken";
import {
  getThemaAantallen,
  getThemaWeken,
} from "#/modules/les.planning/domain/jaarplanning/verdeling";
import { getLegeWeken } from "#/modules/les.planning/domain/jaarplanning/voortgang";
import { getYearWeeks } from "#/modules/les.planning/domain/jaarplanning/weeks";
import { getLessenPerWeek } from "#/modules/les.planning/domain/lesnummer";
import { changeJaarplanningAtom } from "#/modules/les.planning/jaar/jaarplanning-editor";
import { useShownWeeks } from "#/modules/les.planning/jaar/use-shown-weeks";
import {
  jaarplanningQueryOptions,
  planningQueryOptions,
  roosterQueryOptions,
} from "#/modules/les.planning/query.planning";
import {
  JAARPLANNING_SEARCH_DEFAULTS,
  jaarplanningSearchSchema,
} from "#/modules/les.planning/schema.search";
import { PlanningProvider, useUndo } from "#/modules/les.planning/services/runtime";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/planning/year",
)({
  validateSearch: jaarplanningSearchSchema,
  search: { middlewares: [stripSearchParams(JAARPLANNING_SEARCH_DEFAULTS)] },
  loader: ({ context, params: { school, schoolyear } }) =>
    Promise.all([
      context.queryClient.ensureQueryData(jaarplanningQueryOptions({ school, schoolyear })),
      context.queryClient.ensureQueryData(roosterQueryOptions({ school, schoolyear })),
    ]),
  component: JaarplanningPage,
});

function JaarplanningPage() {
  const { school, schoolyear } = Route.useParams();
  return (
    <PlanningProvider tenant={{ school, schoolyear }}>
      <Jaarplanning />
    </PlanningProvider>
  );
}

function Jaarplanning() {
  const { school, schoolyear } = Route.useParams();
  const navigate = Route.useNavigate();
  const query = useQuery(jaarplanningQueryOptions({ school, schoolyear }));
  const rooster = useQuery(roosterQueryOptions({ school, schoolyear }));
  const { week, unplanned } = Route.useSearch();
  const history = useUndo();
  const change = useAtomSet(changeJaarplanningAtom);
  const [printing, setPrinting] = useState(false);
  const planning = query.data ?? EMPTY_JAARPLANNING;
  const weeks = getYearWeeks(getSchooljaarRange(schoolyear));
  const { vakanties } = planning;
  const planningWeeks = getPlanningWeeks(weeks, vakanties);
  const timeZone = getLocalTimeZone();
  const todayDate = today(timeZone);
  const range = getSchooljaarRange(schoolyear);
  const regels = rooster.data?.regels ?? [];
  const lessenPerWeek = getLessenPerWeek(regels, todayDate);
  // The whole schooljaar of lessen, for what a les the jaarplanning leaves empty was about.
  const lessenQuery = useQuery(
    planningQueryOptions(
      { school, schoolyear },
      {
        from: toAbsoluteIso(range.start, 0, timeZone),
        to: toAbsoluteIso(range.end.add({ days: 1 }), 0, timeZone),
      },
    ),
  );
  const lessenData = lessenQuery.data?.lessen;
  const regelsData = rooster.data?.regels;
  const uitLessen = useMemo(
    () => getUitLessen({ lessen: lessenData ?? [], regels: regelsData ?? [], timeZone }),
    [lessenData, regelsData, timeZone],
  );
  const dezeWeek = getWeekStart(todayDate);
  const inSchooljaar = todayDate.compare(range.start) >= 0 && todayDate.compare(range.end) <= 0;
  const aantalLessen = getAantalLessen(planning, lessenPerWeek);
  const themaAantallen = useMemo(
    () => getThemaAantallen(planning, planningWeeks, vakanties),
    [planning, planningWeeks, vakanties],
  );
  const themaWeken = useMemo(
    () => getThemaWeken(planning, planningWeeks, vakanties),
    [planning, planningWeeks, vakanties],
  );
  const shownWeeks = useShownWeeks(
    planningWeeks,
    getLegeWeken(planningWeeks, planning, aantalLessen, vakanties),
    unplanned,
  );

  async function printPdf() {
    setPrinting(true);
    try {
      const { downloadJaarplanningPdf } =
        await import("#/modules/les.planning/jaar/jaarplanning-pdf");
      await downloadJaarplanningPdf({
        van: range.fromYear,
        weeks: planningWeeks,
        planning,
        aantalLessen,
        vakanties,
      });
    } finally {
      setPrinting(false);
    }
  }

  function setOnlyUnplanned(next: boolean) {
    void navigate({
      search: (prev) => ({ ...prev, unplanned: next, week: undefined }),
      replace: true,
    });
  }

  // A week the filter hides opens with all weeks shown; the grid scrolls to it once it is there.
  // It goes to the top, so the weeks after it are in view as well.
  function openWeek(target: string) {
    if (!unplanned || shownWeeks.some((item) => item.start.toString() === target)) {
      scrollGridToWeek(target, { behavior: "smooth", flash: true, align: "start" });
      return;
    }
    void navigate({
      search: (prev) => ({ ...prev, unplanned: false, week: target }),
      replace: true,
    });
  }

  return (
    <Page>
      <PlanningHeader
        actions={
          <>
            <IconButton
              size="icon-lg"
              className="size-8 min-w-8"
              label={m["planning.jaar.ongedaan"]()}
              disabled={!history.canUndo}
              onClick={history.undo}
            >
              <IconMaterialSymbolsUndoRounded />
            </IconButton>
            <IconButton
              size="icon-lg"
              className="size-8 min-w-8"
              label={m["planning.jaar.herhalen"]()}
              disabled={!history.canRedo}
              onClick={history.redo}
            >
              <IconMaterialSymbolsRedoRounded />
            </IconButton>
            <IconButton
              size="icon-lg"
              className="size-8 min-w-8"
              label={m["planning.jaar.pdf_downloaden"]()}
              disabled={printing}
              onClick={() => void printPdf()}
              data-testid="jaarplanning-pdf"
            >
              <IconMaterialSymbolsPrintOutline />
            </IconButton>
            <Separator
              orientation="vertical"
              className="mx-1.5 h-4.5 data-[orientation=vertical]:self-center"
            />
            <ConfirmButton
              variant="ghost"
              size="sm"
              aria-label={m["planning.jaar.standaard_toepassen"]()}
              confirmTitle={m["planning.jaar.standaard_titel"]()}
              confirmMessage={m["planning.jaar.standaard_uitleg_lessen"]({
                nummer: STANDAARD_VAK,
              })}
              confirmLabel={m["planning.jaar.standaard_vervangen"]({
                nummer: STANDAARD_VAK,
              })}
              onClick={() =>
                change({
                  kind: "applyStandaard",
                  weeks: getStandaardWeeks(weeks, vakanties),
                  lessen: aantalLessen,
                })
              }
            >
              <IconMaterialSymbolsAutoAwesomeOutlineRounded />
              {m["planning.jaar.standaard_kort"]()}
            </ConfirmButton>
          </>
        }
      />
      <Page.Body className={cn(containerStyles({ container: "default" }), "mt-0 pb-10")}>
        <PendingContent pending={query.isFetching} className="-mx-(--container-gutter)">
          <JaarplanningGrid
            focusWeek={week}
            weeks={shownWeeks}
            overview={
              <JaarplanningOverview
                van={range.fromYear}
                weeks={planningWeeks}
                planning={planning}
                aantalLessen={aantalLessen}
                vakanties={vakanties}
                themaAantallen={themaAantallen}
                dezeWeek={inSchooljaar ? dezeWeek.toString() : null}
                onlyUnplanned={unplanned}
                onOnlyUnplannedChange={setOnlyUnplanned}
                onOpenWeek={openWeek}
              />
            }
            planning={planning}
            aantalLessen={aantalLessen}
            vakanties={vakanties}
            themaAantallen={themaAantallen}
            themaWeken={themaWeken}
            uitLessen={uitLessen}
            onChangeVak={(plek, inhoud) => change({ kind: "setVak", plek, inhoud })}
            onChangeOpmerking={(week, tekst) => change({ kind: "setOpmerking", week, tekst })}
            onAddVak={() => change({ kind: "addVak" })}
            onRemoveVak={(vak) => change({ kind: "removeVak", vak })}
          />
        </PendingContent>
      </Page.Body>
    </Page>
  );
}
